import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import SvgIcons from '../../../services/SvgIcons';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../../../contexts/AuthContext';
import strapiClient from '../../../services/strapi/config';
import './PaymentMethodPage.scss';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const stripeAppearance = {
    theme: 'stripe',
    variables: {
        colorPrimary: '#6D4F47',
        colorBackground: '#ffffff',
        colorText: '#30313d',
        colorDanger: '#e74c3c',
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: '8px'
    }
};

// Formulaire Stripe interne
const UpdateCardForm = ({ user, onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setIsProcessing(true);
        setError(null);

        try {
            const { error: submitError } = await elements.submit();
            if (submitError) { setError(submitError.message); return; }

            // Confirmer le SetupIntent
            const { error: confirmError, setupIntent } = await stripe.confirmSetup({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/mon-compte/moyen-de-paiement?updated=true`
                },
                redirect: 'if_required'
            });

            if (confirmError) {
                setError(confirmError.message);
                return;
            }

            // Mettre à jour les plans avec la nouvelle carte
            if (setupIntent?.payment_method) {
                await strapiClient.post('/api/payment/update-payment-method', {
                    customerEmail: user.email,
                    paymentMethodId: setupIntent.payment_method
                });
                onSuccess();
            }
        } catch (err) {
            setError(err.message || 'Une erreur est survenue');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="payment-method-page__form">
            <div className="payment-method-page__stripe-element">
                <PaymentElement options={{ wallets: { link: 'never' } }} />
            </div>
            {error && <p className="payment-method-page__error">❌ {error}</p>}
            <div className="payment-method-page__actions">
                <Link to="/mon-compte" className="payment-method-page__cancel">Annuler</Link>
                <button type="submit" disabled={isProcessing || !stripe} className="payment-method-page__submit">
                    {isProcessing ? 'Enregistrement...' : 'Enregistrer ma nouvelle carte'}
                </button>
            </div>
        </form>
    );
};

const PaymentMethodPage = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const [clientSecret, setClientSecret] = useState(null);
    const [plans, setPlans] = useState([]);
    const [savedCard, setSavedCard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        window.scroll(0, 0);
        
        if (!isAuthenticated) { navigate('/connexion'); return; }

        // Vérifier si on revient d'une redirection Stripe
        const params = new URLSearchParams(window.location.search);
        if (params.get('updated') === 'true') {
            setSuccess(true);
            setLoading(false);
            return;
        }

        const init = async () => {
            try {
                const token = localStorage.getItem('strapiToken');

                // Récupérer les plans, la carte sauvegardée et créer le SetupIntent en parallèle
                const [plansResponse, setupResponse, cardResponse] = await Promise.all([
                    strapiClient.get('/api/purchases/my-installment-plans', {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    strapiClient.post('/api/payment/setup-intent', {
                        customerEmail: user.email
                    }),
                    strapiClient.get(`/api/payment/saved-card?email=${encodeURIComponent(user.email)}`)
                ]);

                setPlans(plansResponse.data.data || []);
                setClientSecret(setupResponse.data.clientSecret);
                setSavedCard(cardResponse.data.card || null);
            } catch (err) {
                setError('Impossible de charger la page. Réessaie plus tard.');
            } finally {
                setLoading(false);
            }
        };

        if (user?.email) init();
    }, [isAuthenticated, user, navigate]);

    const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric'
    });

    return (
        <div className="payment-method-page">
            <Helmet>
                <title>Mon moyen de paiement | Mon compte | Grandir et s'élever ensemble</title>
            </Helmet>

            <div className="payment-method-page__container">
                <div className="payment-method-page__header">
                    <Link to="/mon-compte" className="payment-method-page__back">
                        <SvgIcons name="chevron_back" width="20" height="20" color="currentColor" />
                        Mon compte
                    </Link>
                    <h1>Mon moyen de paiement</h1>
                </div>

                {loading && <p className="payment-method-page__loading">Chargement...</p>}
                {error && <p className="payment-method-page__error">{error}</p>}

                {success && (
                    <div className="payment-method-page__success">
                        <div className="payment-method-page__success-icon">
                            <SvgIcons name="check_circle" width="64" height="64" color="#22c55e" />
                        </div>
                        <h2>Carte mise à jour !</h2>
                        <p>Ta nouvelle carte bancaire a bien été enregistrée.<br/>Tes prochains prélèvements seront effectués sur cette carte.</p>
                        <Link to="/mon-compte" className="payment-method-page__btn">Retour à mon compte</Link>
                    </div>
                )}

                {!loading && !error && !success && (
                    <>
                        {/* Récap des plans actifs */}
                        {plans.length > 0 && (
                            <div className="payment-method-page__plans">
                                <h2>Tes paiements échelonnés en cours</h2>
                                <ul>
                                    {plans.map(plan => (
                                        <li key={plan.id} className="payment-method-page__plan">
                                            <span className="payment-method-page__plan-title">{plan.productId}</span>
                                            <div className="payment-method-page__plan-installments">
                                                {plan.installments.map(inst => (
                                                    <div key={inst.installmentNumber} className={`payment-method-page__installment payment-method-page__installment--${inst.status}`}>
                                                        <span>Versement {inst.installmentNumber}/{plan.totalInstallments}</span>
                                                        <span>{inst.amount}€</span>
                                                        <span>{formatDate(inst.dueDate)}</span>
                                                        <span className="payment-method-page__inst-status">
                                                            {inst.status === 'pending' ? '✅ Payé' :
                                                             inst.status === 'scheduled' ? '⏳ À venir' :
                                                             inst.status === 'processing' ? '🔄 En cours' : inst.status}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Carte actuelle */}
                        {savedCard && (
                            <div className="payment-method-page__current-card">
                                <h2>Carte enregistrée</h2>
                                <div className={`payment-method-page__card-display ${savedCard.isExpired ? 'payment-method-page__card-display--expired' : ''}`}>
                                    <div className="payment-method-page__card-brand">
                                        {savedCard.brand.charAt(0).toUpperCase() + savedCard.brand.slice(1)}
                                    </div>
                                    <div className="payment-method-page__card-number">
                                        <span>**** **** ****</span>
                                        <span className="payment-method-page__card-last4">{savedCard.last4}</span>
                                    </div>
                                    <div className="payment-method-page__card-expiry">
                                        <span>Expire le </span>
                                        <span>{String(savedCard.exp_month).padStart(2, '0')}/{savedCard.exp_year}</span>
                                        {savedCard.isExpired && (
                                            <span className="payment-method-page__card-expired-badge">Expirée</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Formulaire mise à jour carte */}
                        <div className="payment-method-page__update-section">
                            <h2>Mettre à jour ma carte bancaire</h2>
                            <p className="payment-method-page__info">
                                {plans.length > 0
                                    ? `Cette carte sera utilisée pour ${plans.length > 1 ? `tes ${plans.length} plans` : 'ton plan'} de paiement en cours.`
                                    : 'Tu n\'as pas de paiement échelonné en cours.'
                                }
                            </p>

                            {clientSecret && (
                                <Elements stripe={stripePromise} options={{
                                    clientSecret,
                                    appearance: stripeAppearance,
                                    wallets: { link: 'never' }
                                }}>
                                    <UpdateCardForm
                                        user={user}
                                        onSuccess={() => setSuccess(true)}
                                    />
                                </Elements>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentMethodPage;
